// Phase 122 — Save-to-folder modal.
//
// Fires immediately after a teacher creates a standalone artifact
// (test, performance task, project, etc.). The auto-file rule
// (server-side) has already filed the item into a matching Subject
// or Unit folder when possible; this modal:
//   • Surfaces WHERE the item was filed so it never feels lost
//   • Offers a one-click way to confirm the suggested location
//   • Offers an escape hatch to pick a different folder
//
// Design choices (locked with user):
//   • Smart default — pre-select the auto-filed folder, big primary
//     "Save to <folder>" button. "Choose different folder…" link
//     reveals the folder tree.
//   • Unfiled is shown as the default when no folder matched. The
//     teacher can still proactively file it from the modal.
//
// Usage:
//   <SaveToFolderModal
//     open={!!savedStub}
//     activity={savedStub}
//     folders={folders}
//     onClose={() => setSavedStub(null)}
//     onMoved={(folderId) => {/* refresh */}}
//   />
//
// `activity` should be the Library stub returned by /api/activities
// for the newly-created item (the stub's `folder_id` is the
// auto-suggested folder).

import { useEffect, useMemo, useState } from "react";
import { Inbox, FolderInput, Folder, FolderOpen, Check, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { moveActivityToFolder } from "../lib/api";
import { styleForActivity } from "../lib/typePalette";

export function SaveToFolderModal({ open, activity, folders, onClose, onMoved }) {
  const [pickingDifferent, setPickingDifferent] = useState(false);
  const [pendingFolderId, setPendingFolderId] = useState(activity?.folder_id ?? null);
  const [busy, setBusy] = useState(false);

  // Reset state when the modal re-opens for a fresh item.
  useEffect(() => {
    if (open) {
      setPickingDifferent(false);
      setPendingFolderId(activity?.folder_id ?? null);
      setBusy(false);
    }
  }, [open, activity?.id, activity?.folder_id]);

  const palette = styleForActivity(activity);
  const subjectFolders = useMemo(
    () => (folders || []).filter((f) => !f.parent_folder_id),
    [folders]
  );
  const unitFoldersBySubject = useMemo(() => {
    const map = new Map();
    (folders || []).forEach((f) => {
      if (f.parent_folder_id) {
        const list = map.get(f.parent_folder_id) || [];
        list.push(f);
        map.set(f.parent_folder_id, list);
      }
    });
    return map;
  }, [folders]);

  const suggestedFolder = useMemo(
    () => (activity?.folder_id ? (folders || []).find((f) => f.id === activity.folder_id) : null),
    [folders, activity?.folder_id]
  );

  if (!open || !activity) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      // If teacher didn't change anything, the auto-file already filed it.
      if ((pendingFolderId ?? null) !== (activity.folder_id ?? null)) {
        await moveActivityToFolder(activity.id, pendingFolderId ?? null);
      }
      onMoved?.(pendingFolderId ?? null);
      onClose?.();
    } catch (e) {
      console.error("SaveToFolderModal move failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="save-to-folder-modal">
        <DialogHeader>
          <DialogTitle className="font-black text-lg text-[#1c2d3d]">
            Where should we save this?
          </DialogTitle>
        </DialogHeader>

        {/* Header — what was just created */}
        <div className="rounded-lg border border-[#1c2d3d]/10 p-3 bg-[#f8fafb]">
          <div className="flex items-center gap-2">
            <span
              data-testid="save-modal-type-pill"
              className="inline-flex items-center font-bold uppercase text-[9px] tracking-[0.18em] px-2 py-0.5 rounded-full"
              style={{ backgroundColor: palette.bg, color: palette.fg }}
            >
              {palette.label}
            </span>
            <span className="text-sm font-bold text-[#1c2d3d] truncate" title={activity.activity_name}>
              {activity.activity_name}
            </span>
          </div>
        </div>

        {!pickingDifferent ? (
          <div className="space-y-3">
            <div
              data-testid="save-modal-suggested"
              className="rounded-lg border-2 border-[#96ded1] bg-[#96ded1]/15 p-4 flex items-start gap-3"
            >
              <div
                className="h-10 w-10 rounded-md flex items-center justify-center shrink-0"
                style={{ backgroundColor: suggestedFolder?.color || "#cbd5e1" }}
              >
                {suggestedFolder ? (
                  <FolderOpen className="h-5 w-5 text-[#1c2d3d]" />
                ) : (
                  <Inbox className="h-5 w-5 text-[#1c2d3d]" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold uppercase text-[10px] tracking-[0.18em] text-[#4a6984]/70">
                  Suggested location
                </p>
                <p className="mt-0.5 font-extrabold text-[#1c2d3d] text-base truncate">
                  {suggestedFolder ? suggestedFolder.name : "Unfiled"}
                </p>
                {suggestedFolder?.kind === "unit" && (
                  <p className="text-xs text-[#4a6984] mt-0.5">
                    Inside{" "}
                    {(folders || []).find((f) => f.id === suggestedFolder.parent_folder_id)?.name ||
                      "subject folder"}
                  </p>
                )}
                {!suggestedFolder && (
                  <p className="text-xs text-[#4a6984] mt-0.5">
                    No matching folder yet — you can move it later.
                  </p>
                )}
              </div>
              <Check className="h-5 w-5 text-[#10b981] shrink-0" />
            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="button"
                onClick={confirm}
                disabled={busy}
                data-testid="save-modal-confirm"
                className="w-full bg-[#96ded1] hover:bg-[#96ded1]/85 text-[#1c2d3d] font-extrabold uppercase tracking-[0.14em] text-xs"
              >
                {busy ? "Saving…" : suggestedFolder ? `Save to ${suggestedFolder.name}` : "Keep in Unfiled"}
              </Button>
              <button
                type="button"
                data-testid="save-modal-pick-different"
                onClick={() => setPickingDifferent(true)}
                className="text-xs font-bold uppercase tracking-[0.14em] text-[#4a6984] hover:text-[#1c2d3d] underline-offset-4 hover:underline"
              >
                Choose a different folder…
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <FolderTreePicker
              folders={folders}
              subjectFolders={subjectFolders}
              unitFoldersBySubject={unitFoldersBySubject}
              value={pendingFolderId}
              onChange={setPendingFolderId}
            />
            <div className="flex items-center justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => setPickingDifferent(false)}
                data-testid="save-modal-back"
                className="text-xs font-bold uppercase tracking-[0.14em] text-[#4a6984] hover:text-[#1c2d3d]"
              >
                ← Back
              </button>
              <Button
                type="button"
                onClick={confirm}
                disabled={busy}
                data-testid="save-modal-confirm-picked"
                className="bg-[#96ded1] hover:bg-[#96ded1]/85 text-[#1c2d3d] font-extrabold uppercase tracking-[0.14em] text-xs"
              >
                {busy ? "Saving…" : "Save here"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FolderTreePicker({ subjectFolders, unitFoldersBySubject, value, onChange }) {
  return (
    <div
      data-testid="save-modal-folder-tree"
      className="max-h-72 overflow-y-auto rounded-lg border border-[#1c2d3d]/10"
    >
      <FolderRow
        kind="unfiled"
        label="Unfiled"
        active={value === null}
        onClick={() => onChange(null)}
        testid="save-modal-folder-unfiled"
      />
      {subjectFolders.map((s) => (
        <div key={s.id}>
          <FolderRow
            kind="subject"
            label={s.name}
            color={s.color}
            active={value === s.id}
            onClick={() => onChange(s.id)}
            testid={`save-modal-folder-${s.id}`}
          />
          {(unitFoldersBySubject.get(s.id) || []).map((u) => (
            <FolderRow
              key={u.id}
              kind="unit"
              label={u.name}
              color={u.color}
              active={value === u.id}
              onClick={() => onChange(u.id)}
              testid={`save-modal-folder-${u.id}`}
            />
          ))}
        </div>
      ))}
      {subjectFolders.length === 0 && (
        <p className="px-3 py-3 text-xs text-[#4a6984]">
          You don&apos;t have any folders yet. Create one from the Library sidebar.
        </p>
      )}
    </div>
  );
}

function FolderRow({ kind, label, color, active, onClick, testid }) {
  const indent = kind === "unit";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={
        "w-full text-left px-3 py-2 flex items-center gap-2 transition-colors " +
        (active
          ? "bg-[#96ded1]/30 text-[#1c2d3d]"
          : "hover:bg-[#f4f5f7] text-[#1c2d3d]") +
        (indent ? " pl-9" : "")
      }
    >
      {kind === "unfiled" ? (
        <Inbox className="h-4 w-4 text-[#4a6984]" />
      ) : (
        <span
          className="h-4 w-4 rounded-sm border border-[#1c2d3d]/15 shrink-0"
          style={{ backgroundColor: color || "#cbd5e1" }}
        />
      )}
      <span className="text-sm font-bold truncate">{label}</span>
      {active && <Check className="h-4 w-4 text-[#10b981] ml-auto shrink-0" />}
    </button>
  );
}

export default SaveToFolderModal;
